import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerCloseButton,
  DrawerBody,
  Button,
  Text,
  Box,
  Flex,
  Badge,
  IconButton,
  Center,
  Spinner,
} from "@chakra-ui/react";
import FileListView from "./filelist";
import { useTranslation } from "react-i18next";
import FileUploadIcon from "@root/assets/icons/fileuploadicon";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Paginate } from "../pagination";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { useEffect, useRef } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  files: FileItem[];
  selectedFolder: { id: string; name: string } | null;
  onCreateClick: () => void;
  folderDrawerOnOpen: () => void;
  fileCount: number;
  filePage: number;
  setFilePage: (page: number) => void;
  deleteFileHandler: (payload: FileData) => void;
  retryFileHandler?: (payload: FileData) => void;
  fileLoading: boolean;
  getFiles: (payload: any, skipLoading?: boolean) => Promise<void>;
}

const FileDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  files,
  selectedFolder,
  onCreateClick,
  folderDrawerOnOpen,
  fileCount,
  filePage,
  setFilePage,
  deleteFileHandler,
  retryFileHandler,
  fileLoading,
  getFiles,
}) => {
  const { t } = useTranslation("common");
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Polling effect - only runs when drawer is open
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (isOpen && selectedFolder?.id) {
      // Start polling every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        getFiles(
          {
            folderId: selectedFolder.id,
            limit: PAGINATION_DEFAULTS.PAGESIZE,
            page: filePage,
          },
          true // skipLoading = true for polling calls
        );
      }, 3000);
    }

    // Cleanup interval on unmount or when drawer closes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isOpen, selectedFolder?.id, filePage, getFiles]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg="white" h="100vh" overflow="hidden">
        <DrawerCloseButton />

        {/* Fixed Header */}
        <DrawerHeader
          fontWeight="semibold"
          fontSize="md"
          borderBottomWidth="1px"
          borderColor="gray.200"
          flexShrink={0}
          py={3}
          px={4}
        >
          <Flex align="center" gap={2}>
            <IconButton
              aria-label={t("projects.backToFolders")}
              p={1.5}
              icon={<ArrowBackIcon />}
              size="sm"
              variant="ghost"
              onClick={() => {
                onClose();
                folderDrawerOnOpen();
              }}
            />
            <Text fontSize="md">
              {selectedFolder?.name || t("projects.noFolderSelected")}
            </Text>
          </Flex>
        </DrawerHeader>

        <DrawerBody
          display="flex"
          flexDirection="column"
          p={0}
          overflow="hidden"
          flex="1"
          minH={0}
        >
          {/* Scrollable file list area */}
          <Box
            flex="1"
            minH={0}
            overflowY="scroll"
            p={2}
            display="flex"
            flexDirection="column"
            onWheel={(e) => e.stopPropagation()} // ← Prevent scroll bubbling
            sx={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y", // ← Allow vertical touch scroll
              "&::-webkit-scrollbar": {
                width: "2px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#F1F1F1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#A0AEC0",
                borderRadius: "4px",
                "&:hover": {
                  background: "#718096",
                },
              },
            }}
          >
            <Flex
              alignItems="center"
              gap={1.5}
              mb={1.5}
              justifyContent="space-between"
            >
              <Text fontSize="xs" color="gray.500">
                {t("projects.files")}
              </Text>
              <Badge
                border="1px solid #40599B"
                px={1.5}
                py={0.5}
                fontSize="10px"
                fontWeight={400}
                borderRadius="full"
                bgColor="#EAF5FA"
                color="#40599B"
              >
                {files?.length || 0}{" "}
                {files?.length === 1
                  ? t("projects.file")
                  : t("projects.filesLabel")}
              </Badge>
            </Flex>

            {fileLoading ? (
              <Center flex="1" minH="150px">
                <Spinner size="sm" thickness="2px" />
              </Center>
            ) : files && Array.isArray(files) && files.length > 0 ? (
              <FileListView
                files={files}
                onDeleteClick={deleteFileHandler}
                onRetryClick={retryFileHandler}
                fileLoading={fileLoading}
              />
            ) : !fileLoading ? (
              <Center flex="1" minH="150px">
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {t("projects.noFile")}
                </Text>
              </Center>
            ) : null}
          </Box>

          {/* Fixed footer area */}
          <Box
            flexShrink={0}
            borderTopWidth="1px"
            borderColor="gray.200"
            p={2}
            bg="white"
          >
            <Flex direction="column" gap={2}>
              <Flex justify="flex-end">
                <Button
                  bg="#0E0E0E"
                  color="white"
                  leftIcon={<FileUploadIcon />}
                  onClick={onCreateClick}
                  _hover={{ bg: "#2D2D2D" }}
                  size="sm"
                  fontSize="xs"
                  height="32px"
                  px={3}
                >
                  {t("projects.uploadFile")}
                </Button>
              </Flex>

              <Flex justify="center">
                <Paginate
                  page={filePage}
                  count={fileCount}
                  pageSize={PAGINATION_DEFAULTS.PAGESIZE}
                  onPageChange={(page: number) => setFilePage(page)}
                />
              </Flex>
            </Flex>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default FileDrawer;
